/**
 * It starts the WebSocket, subscribes to channels and registers the incoming messages listener
 * @param {Object} config Config object to init WebSocket
 * @param {String} config.uuid Unique user session's identifier on WebSocket
 * @param {String} config.authKey Key to communicate with private channels in the WebSocket
 * @param {String} config.publishKey Key to be able to publish in WebSocket
 * @param {String} config.subscribeKey Key to be able to subscribe to WebSocket's channels
 */
export async function initWebSocket({ uuid, authKey, subscribeKey, publishKey } = {}) {
  if (!Domestika.webSocket) {
    return null;
  }

  return Domestika.webSocket.init({ uuid, authKey, subscribeKey, publishKey });
}

/**
 * It registers a listener according to the type provided
 * @param {Object} config Config object to register the listener
 * @param {String} config.listenerType Type of Listener to be registered
 * @param {Object} config.channelListenerCallback Config object to register listeners, mapped to the channels's name
 */
function addListener({ listenerType, channelListenerCallback } = {}) {
  if (!Domestika.webSocket) {
    return null;
  }

  return Domestika.webSocket.listen({
    listener: {
      [listenerType]: (content) => {
        channelListenerCallback[content.subscribedChannel](content);
      },
    },
  });
}

/**
 * It registers the incoming messages listener
 * @param {Object} config Config object to init WebSocket
 * @param {Object} config.messagesListener Config object to register listeners to incoming messages, mapped to the channels's name
 */
export function addMessageListener({ messagesListener } = {}) {
  return addListener({
    listenerType: "message",
    channelListenerCallback: messagesListener,
  });
}

/**
 * It registers the incoming presence events listener
 * @param {Object} config Config object to init WebSocket
 * @param {Object} config.messagesListener Config object to register listeners to incoming presence events, mapped to the channels's name
 */
export function addPresenceListener({ presenceListener } = {}) {
  return addListener({
    listenerType: "presence",
    channelListenerCallback: presenceListener,
  });
}

/**
 * It subscribes to channels
 * @param {Object} config Config object to add subscriptions
 * @param {Array} config.channels List of channels to subscribe to
 * @param {String} config.channels.channelName Name of the Channel
 * @param {Boolean} config.channels.withPresence Include presence channel subscription
 */
export function subscribeChannels({ channels } = {}) {
  if (!Domestika.webSocket) {
    return null;
  }

  channels.forEach(({ channelName, withPresence }) => {
    Domestika.webSocket.subscribe({ channels: [channelName], withPresence });
  });

  return true;
}

/**
 * It gets the occupancy of a channel
 * @param {Object} config Config object to get channel occupancy
 * @param {String} config.channelName Name of the channel
 */
export async function getChannelOccupancy({ channelName } = {}) {
  if (!Domestika.webSocket) {
    return null;
  }

  return Domestika.webSocket.getChannelOccupancy({
    channelName,
  });
}

/**
 * It publishes a message on a channel
 * @param {Object} config Config object to get channel occupancy
 * @param {String} config.channelName Name of the channe
 * @param {Object} config.message Object that contains the message attributes
 * @param {Object} config.options Object containing optional attributes for the publish
 */
export async function sendMessage({ channelName, message, options = {} } = {}) {
  if (!Domestika.webSocket) {
    return null;
  }

  return Domestika.webSocket.send({
    channel: channelName,
    message,
    options,
  });
}
